import Launch16 from '@carbon/icons-react/es/launch/16';
import { Tooltip } from 'antd';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import { SortOrder } from 'antd/lib/table/interface';
import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Avatar } from 'components/atoms/avatar';
import {
  ColumnHeader,
  NameCell,
  NumberCell,
  WithEllipsis,
} from 'components/organisms/projects-report/common';
import { DateRange } from 'components/organisms/projects-report/common/range-picker/types';
import {
  FormatTime,
  formatTime,
} from 'components/organisms/timesheets/common/format-time';
import { BaseCell } from 'components/organisms/timesheets/common/timesheet-table';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useTranslation } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { sortByText } from 'utils/sort-by-text';
import { numberSorter } from 'utils/sorters';
import { getUrlForPeopleTimesheetReport } from '../../common';
import { UsersReportRecord } from '../../types';

const HoursTrackedWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  a {
    height: 16px;
    display: inline-block;
  }
`;

export type UsersReportColumn = ColumnType<UsersReportRecord> & {
  titleText?: ReactNode;
  description?: ReactNode;
};

const placeholderForEmpty = '-';

export const useColumnsConfig = (
  noofRows: number,
  range: DateRange,
): ColumnsType<UsersReportRecord> => {
  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );

  return [
    {
      titleText: useTranslation('reports.columns.team_members_with_count', {
        count: noofRows,
      }),
      width: '256px',
      fixed: true,
      key: 'teamMember',
      render: (_, { firstName, lastName, photoUrl }: UsersReportRecord) => {
        const userfullName = fullName({ firstName, lastName });

        return (
          <NameCell
            logo={<Avatar name={userfullName} src={photoUrl} size={18} />}
            primaryText={userfullName}
          />
        );
      },
      sorter: (
        { firstName: firstNameA, lastName: lastNameA }: UsersReportRecord,
        { firstName: firstNameB, lastName: lastNameB }: UsersReportRecord,
      ) => {
        return sortByText(
          fullName({ firstName: firstNameA, lastName: lastNameA }),
          fullName({ firstName: firstNameB, lastName: lastNameB }),
        );
      },
      defaultSortOrder: 'ascend' as SortOrder,
      sortDirections: [
        'ascend' as SortOrder,
        'descend' as SortOrder,
        'ascend' as SortOrder,
      ],
    },
    {
      titleText: useTranslation('reports.columns.role'),
      key: 'role',
      render: (_, { role }: UsersReportRecord) => (
        <WithEllipsis content={role?.roleName ?? placeholderForEmpty} />
      ),
      sorter: (
        { role: a }: UsersReportRecord,
        { role: b }: UsersReportRecord,
      ) => sortByText(a?.roleName, b?.roleName),
    },
    {
      titleText: useTranslation('reports.columns.projects_assigned'),
      render: (_, { projectsAssigned }: UsersReportRecord) => (
        <NumberCell value={projectsAssigned} />
      ),
      sorter: (
        { projectsAssigned: a }: UsersReportRecord,
        { projectsAssigned: b }: UsersReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.tasks.new_assigned'),
      description: useTranslation('reports.columns.tasks.new_assigned.tooltip'),
      render: (_, { taskInfo: { newAssignedCount } }: UsersReportRecord) => (
        <NumberCell value={newAssignedCount} />
      ),
      sorter: (
        { taskInfo: { newAssignedCount: a } }: UsersReportRecord,
        { taskInfo: { newAssignedCount: b } }: UsersReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.tasks.total_assigned'),
      description: useTranslation(
        'reports.columns.tasks.total_assigned.tooltip',
      ),
      render: (_, { taskInfo: { totalAssignedCount } }: UsersReportRecord) => (
        <NumberCell value={totalAssignedCount} />
      ),
      sorter: (
        { taskInfo: { totalAssignedCount: a } }: UsersReportRecord,
        { taskInfo: { totalAssignedCount: b } }: UsersReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.tasks.completed_on_time'),
      render: (
        _,
        { taskInfo: { completedOnTimeCount } }: UsersReportRecord,
      ) => <NumberCell value={completedOnTimeCount} />,
      sorter: (
        { taskInfo: { completedOnTimeCount: a } }: UsersReportRecord,
        { taskInfo: { completedOnTimeCount: b } }: UsersReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.tasks.completed_late'),
      render: (_, { taskInfo: { completedLateCount } }: UsersReportRecord) => (
        <NumberCell value={completedLateCount} />
      ),
      sorter: (
        { taskInfo: { completedLateCount: a } }: UsersReportRecord,
        { taskInfo: { completedLateCount: b } }: UsersReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.tasks.blocked'),
      description: useTranslation('reports.columns.tasks.blocked.tooltip'),
      render: (_, { taskInfo: { blockedCount } }: UsersReportRecord) => (
        <NumberCell value={blockedCount} />
      ),
      sorter: (
        { taskInfo: { blockedCount: a } }: UsersReportRecord,
        { taskInfo: { blockedCount: b } }: UsersReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.tasks.at_risk'),
      description: useTranslation('reports.columns.tasks.at_risk.tooltip'),
      render: (_, { taskInfo: { atRiskCount } }: UsersReportRecord) => (
        <NumberCell value={atRiskCount} />
      ),
      sorter: (
        { taskInfo: { atRiskCount: a } }: UsersReportRecord,
        { taskInfo: { atRiskCount: b } }: UsersReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.messages_and_comments'),
      render: (_, { commentsCount }: UsersReportRecord) => (
        <NumberCell value={commentsCount} />
      ),
      sorter: (
        { commentsCount: a }: UsersReportRecord,
        { commentsCount: b }: UsersReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: <FormattedMessage id="timesheets.column.capacity" />,
      render: (
        _,
        { timeEntryInfo: { capacityInMinutes } }: UsersReportRecord,
      ) => (
        <BaseCell>
          <FormatTime value={capacityInMinutes} showHypenIfZero={false} />
        </BaseCell>
      ),
      sorter: (
        { timeEntryInfo: { capacityInMinutes: a } }: UsersReportRecord,
        { timeEntryInfo: { capacityInMinutes: b } }: UsersReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: <FormattedMessage id="reports.columns.timeoff" />,
      render: (_, { timeOffInfo: { timeOffMinutes } }: UsersReportRecord) => (
        <BaseCell>
          <FormatTime value={timeOffMinutes} />
        </BaseCell>
      ),
      sorter: (
        { timeOffInfo: { timeOffMinutes: a } }: UsersReportRecord,
        { timeOffInfo: { timeOffMinutes: b } }: UsersReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: <FormattedMessage id="reports.columns.allocated.hours" />,
      render: (_, { allocationInfo: { minutes } }: UsersReportRecord) => (
        <BaseCell>
          <FormatTime value={minutes} />
        </BaseCell>
      ),
      sorter: (
        { allocationInfo: { minutes: a } }: UsersReportRecord,
        { allocationInfo: { minutes: b } }: UsersReportRecord,
      ) => numberSorter(a, b),
    },
    ...(isTimesheetFeatureEnabled
      ? [
          {
            titleText: (
              <FormattedMessage id="timesheets.column.hours_tracked" />
            ),
            render: (
              _,
              { userId, timeEntryInfo: { totalTracked } }: UsersReportRecord,
            ) => (
              <BaseCell>
                {totalTracked != null ? (
                  <HoursTrackedWrapper>
                    <FormatTime value={totalTracked} />
                    <Link
                      to={getUrlForPeopleTimesheetReport(userId, range)}
                      target="_blank"
                    >
                      <Launch16 />
                    </Link>
                  </HoursTrackedWrapper>
                ) : (
                  placeholderForEmpty
                )}
              </BaseCell>
            ),
            sorter: (
              { timeEntryInfo: { totalTracked: a } }: UsersReportRecord,
              { timeEntryInfo: { totalTracked: b } }: UsersReportRecord,
            ) => numberSorter(a, b),
          },
          {
            titleText: (
              <FormattedMessage id="timesheets.column.billable_hours" />
            ),
            render: (
              _,
              { timeEntryInfo: { totalBillable } }: UsersReportRecord,
            ) => (
              <BaseCell>
                {totalBillable != null ? (
                  <FormatTime value={totalBillable} showHypenIfZero={false} />
                ) : (
                  placeholderForEmpty
                )}
              </BaseCell>
            ),
            sorter: (
              { timeEntryInfo: { totalBillable: a } }: UsersReportRecord,
              { timeEntryInfo: { totalBillable: b } }: UsersReportRecord,
            ) => numberSorter(a, b),
          },
          {
            titleText: (
              <FormattedMessage id="timesheets.column.non_billable_hours" />
            ),
            render: (
              _,
              { timeEntryInfo: { totalNonBillable } }: UsersReportRecord,
            ) => (
              <BaseCell>
                {totalNonBillable != null ? (
                  <FormatTime
                    value={totalNonBillable}
                    showHypenIfZero={false}
                  />
                ) : (
                  placeholderForEmpty
                )}
              </BaseCell>
            ),
            sorter: (
              { timeEntryInfo: { totalNonBillable: a } }: UsersReportRecord,
              { timeEntryInfo: { totalNonBillable: b } }: UsersReportRecord,
            ) => numberSorter(a, b),
          },
          {
            titleText: <FormattedMessage id="timesheets.column.utilization" />,
            description: (
              <FormattedMessage id="timesheets.summary.tooltips.utilization" />
            ),
            render: (
              _,
              {
                timeEntryInfo: { billableUtilization, totalBillable },
              }: UsersReportRecord,
            ) => {
              if (billableUtilization != null && billableUtilization < 0) {
                return (
                  <BaseCell>
                    <Tooltip
                      title={
                        <FormattedMessage
                          id="reports.billble_utilization.tooltip_for_timeoff"
                          values={{
                            billableTime: formatTime(totalBillable ?? 0),
                          }}
                        />
                      }
                    >
                      ❗️
                    </Tooltip>
                  </BaseCell>
                );
              }

              return (
                <BaseCell>
                  {billableUtilization != null
                    ? `${billableUtilization.toFixed(1)}%`
                    : placeholderForEmpty}
                </BaseCell>
              );
            },
            sorter: (
              { timeEntryInfo: { billableUtilization: a } }: UsersReportRecord,
              { timeEntryInfo: { billableUtilization: b } }: UsersReportRecord,
            ) => numberSorter(a, b),
          },
        ]
      : []),
  ].map((x: UsersReportColumn) => ({
    showSorterTooltip: false,
    width: '200px',
    title: (
      <ColumnHeader
        title={x.titleText}
        description={x.description}
        align={x.align}
      />
    ),
    ...x,
  }));
};
